/**
 * Campaign State Communication Events - Type Definitions (Generic)
 *
 * This module defines generic TypeScript types for campaign state synchronization
 * between the Sugarloaf web add-in and Win32 client.
 *
 * Key Design: Generic types support any action ID type. Components define their
 * own action enums and provide configuration for mapping and completion logic.
 */

/**
 * Payload containing completed action IDs.
 * Used for CampaignStateReceived (Win32 → SDX) and CampaignStateUpdate (SDX → Win32) events.
 *
 * @example
 * // Win32 sends initial state
 * { data: { completedActions: ["ResearcherDialog", "BrainStormDialog"] } }
 *
 * // SDX sends update after user action
 * { data: { completedActions: ["ResearcherDialog", "BrainStormDialog", "AnalystDialog"] } }
 */
export interface CampaignStatePayload {
    /**
     * Wrapper object.
     */
    data: {
        /**
         * Array of action IDs that the user has completed.
         * Action IDs are case-insensitive strings (e.g., "ResearcherDialog", "BrainStormDialog").
         * System performs deduplication and validation before processing.
         */
        completedActions: string[];
    };
}

/**
 * Payload for GetCampaignState event (Win32 → SDX).
 * Contains completed action IDs sent from Win32 on initialization.
 */
export type CampaignStateReceivedPayload = CampaignStatePayload;

/**
 * Payload for CampaignStateResponse event (Win32 → Web).
 * Contains completed action IDs sent from Win32 in response to GetCampaignState request.
 */
export type CampaignStateResponsePayload = CampaignStateReceivedPayload;

/**
 * Payload for CampaignStateUpdate event (SDX → Win32).
 * Contains updated list of completed action IDs after user action.
 */
export type CampaignStateUpdatePayload = CampaignStatePayload;

/**
 * Payload for CampaignCompletion event (SDX → Win32).
 * Simple boolean signal sent when component determines all required actions are completed.
 * Sent AFTER the final CampaignStateUpdate event.
 *
 * @example
 * // When 4th action is completed, two events are sent:
 * // 1. CampaignStateUpdate with all 4 completedActions
 * // 2. CampaignCompletion with completed: true
 * { completed: true }
 */
export interface CampaignCompletionPayload {
    /**
     * Always true when this event is sent - signals campaign is complete.
     */
    completed: boolean;
}

/**
 * Generic campaign configuration for any action type.
 * Components provide this configuration when creating campaign managers.
 *
 * @template TActionId - The action ID type (enum, string union, etc.)
 *
 * @example
 * // Component-specific configuration
 * const config: CampaignConfig<ActionId> = {
 *     componentId: 'trial-countdown',
 *     requiredActions: [ActionId.Action1, ActionId.Action2],
 *     actionMapper: (schemaId) => {
 *         switch (schemaId.toLowerCase()) {
 *             case 'action1': return ActionId.Action1;
 *             case 'action2': return ActionId.Action2;
 *             default: return null;
 *         }
 *     }
 * };
 */
export interface CampaignConfig<TActionId extends string | number> {
    /**
     * Unique identifier for this component (e.g., "trial-countdown", "feature-adoption").
     * Used to maintain separate manager instances for different components.
     */
    componentId: string;

    /**
     * Array of action IDs required for campaign completion.
     * Campaign is complete when all these actions are in completedActions set.
     */
    requiredActions: readonly TActionId[];

    /**
     * Maps action ID strings from Win32 schema to typed action IDs.
     * Performs case-insensitive matching and normalization.
     *
     * @param schemaActionId - Action ID string from Win32 payload
     * @returns Typed action ID or null if unknown (will be ignored with warning)
     *
     * @example
     * actionMapper: (id) => {
     *     switch (id.toLowerCase()) {
     *         case 'action1': return ActionId.Action1;
     *         default: return null;
     *     }
     * }
     */
    actionMapper: (schemaActionId: string) => TActionId | null;

    /**
     * Optional: Custom completion check function.
     * If not provided, defaults to checking all requiredActions are completed.
     *
     * @param completedActions - Set of completed action IDs
     * @returns true if campaign should be considered complete
     *
     * @example
     * // Custom: complete after 3 of 5 actions
     * isComplete: (completed) => completed.size >= 3
     */
    isComplete?: (completedActions: Set<TActionId>) => boolean;

    /**
     * Optional: Serializes action ID to string for Win32 payload.
     * Defaults to .toString() if not provided.
     *
     * @param actionId - Typed action ID
     * @returns String representation for Win32
     */
    serializeActionId?: (actionId: TActionId) => string;
}

/**
 * Type guard: Validates CampaignStatePayload structure.
 * Ensures completedActions is an array of strings.
 *
 * @param obj - Object to validate
 * @returns true if obj is a valid CampaignStatePayload
 */
export function isCampaignStatePayload(obj: unknown): obj is CampaignStatePayload {
    if (!obj || typeof obj !== 'object') {
        return false;
    }

    const payload = obj as CampaignStatePayload;
    return (
        payload.data &&
        typeof payload.data === 'object' &&
        Array.isArray(payload.data.completedActions) &&
        payload.data.completedActions.every(id => typeof id === 'string')
    );
}

// TODO: REplace with a proper telemetry logger
const logger = {
    logInfo: (tag: string, message: string) => {
        console.log(`[${tag}] ${message}`);
    },
    logWarn: (tag: string, message: string) => {
        console.warn(`[${tag}] ${message}`);
    }

};

/**
 * Validates and normalizes incoming payload.
 * Handles null/undefined, unknown IDs, and duplicates.
 *
 * @param payload - Raw payload from Win32
 * @returns Normalized array of action ID strings
 */
export function validateAndNormalizePayload(payload: unknown): string[] {
    // Validate structure
    if (!payload || typeof payload !== 'object') {
        logger.logWarn('[CampaignStateManager]', 'Invalid payload structure, using empty state');
        return [];
    }

    const wrapperObj = payload as { data?: unknown };

    // Check for data wrapper
    if (!wrapperObj.data || typeof wrapperObj.data !== 'object') {
        logger.logWarn('[CampaignStateManager]', 'Missing data wrapper, using empty state');
        return [];
    }

    const data = wrapperObj.data as { completedActions?: unknown };

    // Treat null/undefined as empty array
    if (data.completedActions == null || !Array.isArray(data.completedActions)) {
        console.log('[CampaignStateManager] No completedActions, using empty state');
        return [];
    }

    // Deduplicate silently (case-insensitive)
    const seen = new Set<string>();
    const result: string[] = [];

    for (const action of data.completedActions) {
        if (typeof action !== 'string') continue;

        const normalized = action.toLowerCase();
        if (!seen.has(normalized)) {
            seen.add(normalized);
            result.push(action); // Keep original casing
        }
    }

    return result;
}
